import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConversationDetail, LiveChatVisitorList } from '@nucleus-commerce/api-client/admin';

import { session } from '@/shared/session/session';

import { conversationsApi } from '../api/conversations-api';
import { conversationKeys, orgVisitorsCover } from '../hooks/conversation-keys';
import { playChime, showDesktopNotification } from '../lib/alerts';
import {
  conversationIdOf,
  conversationOf,
  messageOf,
  readOf,
  storeIdOf,
  typingOf,
  visitorOf,
  type ConversationEvent,
} from '../lib/frames';
import { InboxLiveContext, type InboxLive } from '../lib/inbox-live-context';
import { useInboxPrefs } from '../lib/inbox-prefs';
import {
  createInboxSocket,
  type ClientFrame,
  type InboxConnection,
  type ServerFrame,
  type SocketConstructor,
} from '../lib/inbox-socket';
import {
  applyReadReceipt,
  lastMessageId,
  mergeIntoThread,
  type ThreadData,
} from '../lib/thread-cache';
import { TYPING_TTL_MS, useTypingStore } from '../lib/typing-store';
import { applyConversationToVisitors, upsertVisitor } from '../lib/visitors-cache';

const SUBSCRIBE_BATCH = 20;
const INVALIDATE_DELAY_MS = 250;

export interface InboxLiveProviderProps {
  children: ReactNode;
  /** Tests pass a fake; the browser's own otherwise. */
  WebSocket?: SocketConstructor | undefined;
}

const browserSocket = (): SocketConstructor | undefined =>
  typeof window !== 'undefined' && 'WebSocket' in window
    ? (window.WebSocket as unknown as SocketConstructor)
    : undefined;

/**
 * Holds the inbox WebSocket for as long as an inbox route is mounted (D-103): store channels for
 * every accessible store plus the threads on screen. Frames only refresh the query cache; the
 * data itself always comes from REST. Also raises the opt-in sound and desktop alerts.
 */
export function InboxLiveProvider({
  children,
  WebSocket = browserSocket(),
}: InboxLiveProviderProps) {
  const { t } = useTranslation('conversations');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { me } = session.useSession();
  const myId = me?.user.id;
  const status = useInboxPrefs((state) => state.status);
  const [connection, setConnection] = useState<InboxConnection>('off');

  const followed = useRef(new Map<string, { storeId: string; count: number }>());
  const activeRef = useRef<string | null>(null);
  const unreadSeen = useRef(new Map<string, number>());
  const statusRef = useRef(status);
  const socketRef = useRef<ReturnType<typeof createInboxSocket> | null>(null);
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const live = useRef({ t, myId, navigate });
  useEffect(() => {
    live.current = { t, myId, navigate };
  });

  useEffect(() => {
    const send = (frame: ClientFrame) => socketRef.current?.send(frame) ?? false;

    const scheduleListRefresh = () => {
      clearTimeout(invalidateTimer.current);
      invalidateTimer.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: conversationKeys.counts() });
      }, INVALIDATE_DELAY_MS);
    };

    // Patches every cached live visitor list that covers the store: that store's own list and
    // the org-wide lists that are not narrowed to another store. Lists of other stores (a
    // store-pinned inbox) are left alone; a list nobody loaded stays unloaded.
    const patchVisitorLists = (
      storeId: string,
      patch: (list: LiveChatVisitorList | undefined) => LiveChatVisitorList | undefined,
    ) => {
      queryClient.setQueryData<LiveChatVisitorList>(conversationKeys.visitors(storeId), patch);
      const orgLists = queryClient.getQueriesData<LiveChatVisitorList>({
        queryKey: conversationKeys.orgVisitorLists(),
        predicate: (query) => orgVisitorsCover(query.queryKey, storeId),
      });
      for (const [key] of orgLists) queryClient.setQueryData<LiveChatVisitorList>(key, patch);
    };

    // `visitor.updated` carries the whole row, so a shopper who just arrived is inserted at once
    // rather than waiting for the 30 s refetch.
    const onVisitor = (frame: ServerFrame) => {
      const update = visitorOf(frame.data);
      if (!update) return;
      patchVisitorLists(update.storeId, (list) => upsertVisitor(list, update.visitor));
    };

    const alert = (title: string, body: string, event: ConversationEvent) => {
      const prefs = useInboxPrefs.getState();
      const onScreen = activeRef.current === event.id && document.visibilityState === 'visible';
      if (onScreen) return;
      if (prefs.sound) playChime();
      if (prefs.desktop) {
        showDesktopNotification(title, body, () => {
          void live.current.navigate({
            to: '/org/inbox/$storeId/$conversationId',
            params: { storeId: event.storeId, conversationId: event.id },
          });
        });
      }
    };

    const onConversation = (frame: ServerFrame) => {
      const event = conversationOf(frame.data);
      if (!event) return;
      scheduleListRefresh();
      if (event.visitorId) {
        patchVisitorLists(event.storeId, (list) => applyConversationToVisitors(list, event));
      }
      const detailKey = conversationKeys.detail(event.storeId, event.id);
      if (event.trashed) {
        queryClient.removeQueries({ queryKey: detailKey });
      } else {
        const detail = queryClient.getQueryData<ConversationDetail>(detailKey);
        // A ticket's SLA and a conversion change fields the frame does not carry.
        if (
          detail &&
          ((detail.assignee?.id ?? null) !== event.assignedTo ||
            detail.kind !== event.kind ||
            event.kind === 'ticket')
        ) {
          void queryClient.invalidateQueries({ queryKey: detailKey, exact: true });
        } else if (detail) {
          queryClient.setQueryData<ConversationDetail>(detailKey, {
            ...detail,
            status: event.status,
            departmentId: event.departmentId,
            tags: event.tags,
            priority: event.priority,
            staffUnreadCount: event.staffUnreadCount,
            visitorUnreadCount: event.visitorUnreadCount,
            missed: event.missed,
            lastMessageAt: event.lastMessageAt,
            closedAt: event.closedAt,
          });
        }
      }
      const { t: translate, myId: me } = live.current;
      const previous = unreadSeen.current.get(event.id) ?? 0;
      unreadSeen.current.set(event.id, event.staffUnreadCount);
      const forMe = event.assignedTo === null || event.assignedTo === me;
      if (frame.type === 'conversation.created') {
        if (forMe)
          alert(
            translate('alerts.newConversation'),
            translate('alerts.number', { number: event.number }),
            event,
          );
      } else if (forMe && event.status !== 'closed' && event.staffUnreadCount > previous) {
        alert(
          translate('alerts.newMessage'),
          translate('alerts.number', { number: event.number }),
          event,
        );
      }
    };

    const onFrame = (frame: ServerFrame) => {
      switch (frame.type) {
        case 'conversation.created':
        case 'conversation.updated':
          onConversation(frame);
          return;
        case 'message': {
          const message = messageOf(frame.data);
          const entry = message ? followed.current.get(message.conversationId) : undefined;
          if (!message || !entry) return;
          queryClient.setQueryData<ThreadData>(
            conversationKeys.messages(entry.storeId, message.conversationId),
            (data) => mergeIntoThread(data, [message]),
          );
          const typingKey = message.authorType === 'visitor' ? 'visitor' : message.authorId;
          if (typingKey) useTypingStore.getState().clear(message.conversationId, typingKey);
          return;
        }
        case 'message.read': {
          const read = readOf(frame.data);
          const entry = read ? followed.current.get(read.conversationId) : undefined;
          if (!read || !entry || read.by !== 'visitor') return;
          queryClient.setQueryData<ThreadData>(
            conversationKeys.messages(entry.storeId, read.conversationId),
            (data) =>
              applyReadReceipt(data, read.upToMessageId, frame.at ?? new Date().toISOString()),
          );
          return;
        }
        case 'typing': {
          const typing = typingOf(frame.data);
          if (!typing) return;
          const key = typing.authorType === 'visitor' ? 'visitor' : (typing.userId ?? 'staff');
          useTypingStore.getState().set(typing.conversationId, {
            key,
            authorType: typing.authorType,
            name: typing.agent?.displayName ?? null,
            internal: typing.internal ?? false,
            draft: typing.draft ?? null,
            until: Date.now() + TYPING_TTL_MS,
          });
          return;
        }
        case 'visitor.updated':
          onVisitor(frame);
          return;
        case 'presence.updated': {
          const storeId = storeIdOf(frame.data);
          if (storeId)
            void queryClient.invalidateQueries({ queryKey: conversationKeys.presence(storeId) });
          return;
        }
        case 'resync': {
          const conversationId = conversationIdOf(frame.data);
          const entry = conversationId ? followed.current.get(conversationId) : undefined;
          if (conversationId && entry) {
            void queryClient.resetQueries({
              queryKey: conversationKeys.messages(entry.storeId, conversationId),
            });
          }
          return;
        }
        default:
      }
    };

    const subscribeAll = () => {
      const entries = [...followed.current.entries()].map(([id, { storeId }]) => {
        const after = lastMessageId(
          queryClient.getQueryData<ThreadData>(conversationKeys.messages(storeId, id)),
        );
        return after ? { id, after } : { id };
      });
      for (let i = 0; i < entries.length; i += SUBSCRIBE_BATCH) {
        send({ type: 'subscribe', conversations: entries.slice(i, i + SUBSCRIBE_BATCH) });
      }
    };

    const socket = createInboxSocket({
      WebSocket,
      socketUrl: async () => (await conversationsApi.socketTicket()).url,
      onFrame,
      onReady: () => {
        if (statusRef.current === 'away') send({ type: 'presence', status: 'away' });
        subscribeAll();
        // Anything that changed while the socket was down.
        scheduleListRefresh();
      },
      onMode: setConnection,
    });
    socketRef.current = socket;
    if (statusRef.current !== 'invisible') socket.start();
    const prune = setInterval(() => useTypingStore.getState().prune(Date.now()), 1_000);
    return () => {
      clearInterval(prune);
      clearTimeout(invalidateTimer.current);
      socket.stop();
      socketRef.current = null;
    };
  }, [WebSocket, queryClient]);

  // Invisible keeps the socket closed; online and away are a presence frame on a live socket.
  useEffect(() => {
    statusRef.current = status;
    const socket = socketRef.current;
    if (!socket) return;
    if (status === 'invisible') {
      socket.stop();
      return;
    }
    socket.start();
    socket.send({ type: 'presence', status });
  }, [status]);

  const actions = useMemo<Omit<InboxLive, 'connection' | 'polling'>>(
    () => ({
      follow: (storeId, conversationId) => {
        const entry = followed.current.get(conversationId);
        if (entry) {
          entry.count += 1;
        } else {
          followed.current.set(conversationId, { storeId, count: 1 });
          const after = lastMessageId(
            queryClient.getQueryData<ThreadData>(
              conversationKeys.messages(storeId, conversationId),
            ),
          );
          socketRef.current?.send({
            type: 'subscribe',
            conversations: [after ? { id: conversationId, after } : { id: conversationId }],
          });
        }
        return () => {
          const current = followed.current.get(conversationId);
          if (!current) return;
          current.count -= 1;
          if (current.count > 0) return;
          followed.current.delete(conversationId);
          socketRef.current?.send({ type: 'unsubscribe', conversationIds: [conversationId] });
        };
      },
      setActive: (conversationId) => {
        activeRef.current = conversationId;
      },
      sendTyping: (conversationId, internal) =>
        socketRef.current?.send({ type: 'typing', conversationId, internal }) ?? false,
      sendRead: (conversationId, upToMessageId) =>
        socketRef.current?.send({ type: 'read', conversationId, upToMessageId }) ?? false,
    }),
    [queryClient],
  );
  const value = useMemo<InboxLive>(
    () => ({ ...actions, connection, polling: connection !== 'socket' }),
    [actions, connection],
  );

  return <InboxLiveContext.Provider value={value}>{children}</InboxLiveContext.Provider>;
}
